import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navigation } from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Star, TrendingUp, Users, Award, ArrowRight, Calendar, User } from "lucide-react";
import heroImage from "@/assets/hero-tech-reviews.jpg";
import { useState, useEffect } from "react";

const stats = [
  { icon: Star, label: "Expert Reviews", value: "500+" },
  { icon: Users, label: "Happy Readers", value: "100K+" },
  { icon: Award, label: "Awards Won", value: "25+" },
  { icon: TrendingUp, label: "Products Tested", value: "1000+" },
];

const Index = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/backend/public_posts.php")
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch posts');
        return res.json();
      })
      .then(data => {
        setPosts(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const slogan = "Authentic Product Reviews & Trusted Shopping Links";
  const description = "Welcome to Shophentic – your trusted source for honest product reviews and direct shopping links across a wide range of categories. Shop confidently with curated recommendations from Amazon and other reputable marketplaces.";

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Authentic Lifestyle & Tech Product Reviews | Expert Buying Guides"
        description="Discover authentic product reviews for lifestyle and tech products. Expert buying guides, honest recommendations, and trusted shopping links from Amazon and top marketplaces. Make informed purchasing decisions with Shophentic."
        keywords="product reviews, lifestyle products, tech reviews, buying guides, Amazon affiliate, honest reviews, shopping recommendations, gadget reviews, fashion reviews, home products"
        url="https://shophentic.bishalmondal.online"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge variant="secondary" className="text-sm">
                  Trusted by 100K+ readers
                </Badge>
                <h1 className="text-5xl font-bold mb-4">Shophentic</h1>
                <p className="text-xl mb-6">{slogan}</p>
                <p className="mb-8">{description}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/blog">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Browse Reviews
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" size="lg">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={heroImage}
                  alt="Tech review workspace with devices"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-background border rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <span className="font-semibold">Expert Review</span>
                </div>
                <p className="text-sm text-muted-foreground">From our readers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-accent/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary rounded-lg mb-4">
                  <stat.icon className="h-6 w-6 text-primary-foreground" />
                </div>
                <div className="text-3xl font-bold text-foreground mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Latest Reviews & Guides
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Stay up-to-date with our latest expert reviews and comprehensive buying guides
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading posts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Unable to load posts at the moment.</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No reviews available yet.</p>
              <p className="text-sm text-muted-foreground">Check back soon for our latest product reviews and buying guides.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {posts.slice(0, 3).map((post) => (
                  <Card key={post.id} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="secondary">{post.category}</Badge>
                        {post.featured && (
                          <Badge variant="default">Featured</Badge>
                        )}
                      </div>
                      <CardTitle className="group-hover:text-primary transition-colors">
                        <Link to={`/blog/${post.slug || post.id}`}>
                          {post.title}
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent>
                                        <p className="text-muted-foreground mb-4 line-clamp-4">
                    {post.excerpt}
                  </p>
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <Link to={`/blog/${post.slug || post.id}`}>
                        <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                          Read Review
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center">
                <Link to="/blog">
                  <Button size="lg" variant="outline">
                    View All Reviews
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Never Miss a Review
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of tech enthusiasts who rely on our expert recommendations to make smart purchasing decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/blog">
              <Button variant="amazon" size="lg">
                Start Reading
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Index;